import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * On every route change, start the new page at the top (browsers keep the
 * previous scroll position on client-side navigation).
 *
 * Two deliberate exceptions:
 * - A pure language switch (same page, only the `/<lang>` prefix changed):
 *   the reader stays where they were so they don't lose their place.
 * - An explicit `#anchor` in the URL (e.g. `/<lang>/how-to-order#quote`):
 *   scroll to that element instead, matching the native browser behaviour.
 *
 * Rendered once in App, so it covers every route.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation()
  const prevPageRef = useRef<string | null>(null)

  useEffect(() => {
    // path without the leading `/<lang>` segment, so switching language on
    // the same page is not treated as a page change
    const pageKey = pathname.replace(/^\/[^/]+/, '') || '/'
    const samePage = prevPageRef.current === pageKey
    prevPageRef.current = pageKey

    if (hash) {
      // let the URL anchor win
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView()
      return
    }
    if (!samePage) window.scrollTo(0, 0)
  }, [pathname, hash])

  return null
}

export default ScrollToTop
